import { useMutation, useQuery } from '@tanstack/react-query';
import { Play } from 'lucide-react';
import { useMemo, useState } from 'react';
import { MonacoJsonEditor } from '../../components/MonacoJsonEditor';
import { partnerApi } from '../../lib/api';
import { ToolTestResultPanel } from './ToolTestResultPanel';
import styles from './ToolTestPanel.module.css';

interface ToolTestPanelProps {
  toolId: string;
}

export const ToolTestPanel = ({ toolId }: ToolTestPanelProps) => {
  const [businessId, setBusinessId] = useState('');
  const [partnerUserId, setPartnerUserId] = useState('');
  const [scope, setScope] = useState<'partner_user' | 'partner_user:verified'>('partner_user');
  const [argsText, setArgsText] = useState('{}');
  const [argsValid, setArgsValid] = useState(true);

  const { data: tenants } = useQuery({
    queryKey: ['tenants', { fields: ['id', 'name', 'externalId'] }],
    queryFn: () =>
      partnerApi.getTenants(false, {
        limit: 200,
        fields: ['id', 'name', 'externalId'],
        orderBy: 'name',
        orderDirection: 'asc',
      }),
  });
  const tenantList = useMemo(() => {
    return tenants?.data?.results ?? [];
  }, [tenants]);

  const testMutation = useMutation({
    mutationFn: () => {
      const args = argsValid ? (JSON.parse(argsText) as Record<string, unknown>) : {};
      return partnerApi.testTool(toolId, {
        businessId,
        partnerUserId: partnerUserId.trim() ? partnerUserId : undefined,
        scope,
        args,
      });
    },
  });

  const result = testMutation.data?.result;

  return (
    <fieldset className={styles.panel}>
      <legend className={styles.legend}>Test runner</legend>
      <p className={styles.description}>
        Runs the saved tool against a real business + partner_user. Same code path as production —
        claim_enforcement and credentials are evaluated for real.
      </p>
      <div className={styles.fields}>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>Tenant (business)</span>
          <select
            value={businessId}
            onChange={e => setBusinessId(e.target.value)}
            className={styles.selectFull}>
            <option value="">Pick a tenant…</option>
            {tenantList.map(t => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.id.slice(0, 8)}…)
              </option>
            ))}
          </select>
        </label>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>Partner user ID (optional)</span>
          <input
            type="text"
            value={partnerUserId}
            onChange={e => setPartnerUserId(e.target.value)}
            className={styles.monoInput}
            placeholder="UUID — required if claim_enforcement is set"
          />
        </label>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>Scope</span>
          <select
            value={scope}
            onChange={e => setScope(e.target.value as typeof scope)}
            className={styles.select}>
            <option value="partner_user">partner_user</option>
            <option value="partner_user:verified">partner_user:verified</option>
          </select>
        </label>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>Args (JSON)</span>
          <div className={styles.fieldControl}>
            <MonacoJsonEditor
              value={argsText}
              onChange={setArgsText}
              onValidationChange={isValid => setArgsValid(isValid)}
              height="120px"
            />
          </div>
        </label>
        <div className={styles.actions}>
          <button
            type="button"
            onClick={() => testMutation.mutate()}
            disabled={!businessId || !argsValid || testMutation.isPending}
            className={styles.primaryButton}>
            <Play className="h-4 w-4" />
            {testMutation.isPending ? 'Running…' : 'Run test'}
          </button>
          {testMutation.error && (
            <span className={styles.errorText}>{(testMutation.error as Error).message}</span>
          )}
        </div>
        {result && <ToolTestResultPanel result={result} />}
      </div>
    </fieldset>
  );
};
